import { useCallback } from 'react';

import { useRoute } from 'hooks/navigation/use-route';
import { appendObjectToQueryString } from 'navigation/utils';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';

const reloadParam = 'error-reload';

interface IErrorReloadParams {
  [reloadParam]: string;
}

/**
 * This hook is used to reload the app when an error occurs.
 * It will send the user to the home page to avoid a reload loop.
 * It will also set a query parameter to indicate that the app has been reloaded.
 * @param _onRequestReload - Not used. Required for compatibility with the native useReload hook.
 * @returns A function to reload the app.
 */
export function useReload(_onRequestReload: () => void) {
  const { params } = useRoute<IErrorReloadParams>();

  return useCallback(() => {
    logEvent(CustomEventNames.APP_ERROR_RELOAD, EventTypes.Other);

    // Send the user to the home page to avoid a reload loop
    if (params[reloadParam]) {
      window.location.href = '/';
      return;
    }

    params[reloadParam] = '1';
    // This will fire a reload
    window.location.href = appendObjectToQueryString('/', params);
  }, [params]);
}
